import { useState } from "react";
import { LuPackage } from "react-icons/lu";
import { RANKINGS_API_BASE_URL } from "../../api/rankingsApi";
import { colors } from "../../design/tokens";

interface AppIconProps {
  src: string;
  alt: string;
  size?: "small" | "medium" | "large";
  lazy?: boolean;
  className?: string;
  onLoadError?: () => void;
  onLoadSuccess?: () => void;
}

// Size constants
const SIZES = {
  small: { width: 48, height: 48, iconSize: 24, className: "w-12 h-12" },
  medium: { width: 64, height: 64, iconSize: 32, className: "w-16 h-16" },
  large: { width: 80, height: 80, iconSize: 40, className: "w-20 h-20" },
};

export function AppIcon({
  src,
  alt,
  size = "small",
  lazy = true,
  className = "",
  onLoadError,
  onLoadSuccess,
}: AppIconProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const sizeConfig = SIZES[size];

  // API returns relative icon paths like /app-rankings/icons/abc123.png
  // Convert to absolute URL on blackfinch.pl
  const absoluteIconUrl = src.startsWith("http")
    ? src
    : `${RANKINGS_API_BASE_URL}${src}`;

  const handleLoadSuccess = () => {
    setIsLoaded(true);
    setHasError(false);
    onLoadSuccess?.();
  };

  const handleLoadError = () => {
    setHasError(true);
    onLoadError?.();
  };

  return (
    <div className={`relative inline-block flex-shrink-0 ${sizeConfig.className} ${className}`}>
      {/* Loading skeleton - shown while image loads (same color as inactive send button) */}
      {!isLoaded && !hasError && (
        <div
          className={`absolute inset-0 ${colors.primary.blueLoading} rounded-xl animate-pulse`}
        />
      )}

      {/* Image - rendered immediately with native lazy loading */}
      {!hasError && (
        <img
          src={absoluteIconUrl}
          alt={alt}
          className={`w-full h-full rounded-xl object-cover transition-opacity duration-200 ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          loading={lazy ? "lazy" : "eager"}
          referrerPolicy="no-referrer"
          onLoad={handleLoadSuccess}
          onError={handleLoadError}
        />
      )}

      {/* Fallback icon - shown when image fails to load */}
      {hasError && (
        <div
          className={`w-full h-full rounded-xl ${colors.surface.secondary} flex items-center justify-center`}
        >
          <LuPackage size={sizeConfig.iconSize} className={colors.text.secondary} />
        </div>
      )}
    </div>
  );
}
